import pluralize from 'pluralize'
import { useMemo } from 'react'

import { Control } from '@/gen/inventory/v1/control_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'

import { controlImpactPriority } from '@/const/priority'

import { ControlImpact, IMPACT_TO_COLOR, controlImpactLabel } from '@/lib/risk/control-impact'
import { CHART_COLORS } from '@/lib/ui/variants'

import { Swatch } from '@/components/lemmala-design-system/swatch/swatch'
import { AssessmentScoreBadge } from '@/components/shared/assessment-score-badge/assessment-score-badge'

import { groupControlsByImpact } from './control-weight-utils'

type ControlWeightLegendProps = {
  assessmentScore: OngoingAssessment['score']
  controls: Control[]
}

export const ControlWeightLegend = ({ assessmentScore, controls }: ControlWeightLegendProps) => {
  const unvalidatedGroups: { impact: ControlImpact; controls: Control[] }[] = useMemo(() => {
    const grouped = groupControlsByImpact(controls)

    return Object.entries(grouped)
      .filter(([_, group]) => group.weight > 0 && group.type === 'unvalidated')
      .map(([impact, { controls }]) => ({
        impact: impact as ControlImpact,
        controls,
      }))
      .sort((a, b) => controlImpactPriority[a.impact] - controlImpactPriority[b.impact])
  }, [controls])

  return (
    <div>
      <div className='mb-1 text-main-foreground-tertiary text-body-default'>
        Assessment score (in progress)
      </div>
      <AssessmentScoreBadge showTooltip size='large' assessmentScore={assessmentScore} />
      <div className='mt-4 flex flex-col gap-2'>
        {unvalidatedGroups.map((entry) => (
          <div key={entry.impact} className='flex items-center gap-2'>
            <div className='flex items-center gap-1.5 text-main-foreground-primary text-body-default'>
              <Swatch color={CHART_COLORS[IMPACT_TO_COLOR[entry.impact]]} />
              {entry.controls.length} {controlImpactLabel[entry.impact]}{' '}
              {pluralize('control', entry.controls.length)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
